import {
    AfterViewInit,
    Directive,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from "@angular/core";
import {ChartSeries} from "./chart-series";
import * as Highcharts from "highcharts";
import {Chart, Options, SelectEventObject} from "highcharts";
import {MdxMemberUtil} from "../mdx-member-util";
import {ChartMemberSelection} from "./chart-member-selection";
import {MeasureDataType} from "../measure-data-type";
import {Observable, Subscription} from "rxjs";
import {TranslateService} from "@ngx-translate/core";
import expMod from "highcharts/modules/exporting";
import offExpMod from "highcharts/modules/offline-exporting";

expMod(Highcharts);
offExpMod(Highcharts);

export class ChartDataPoint {
    public name: string;

    public y: number;

    constructor(name: string, y: number) {
        this.name = name;
        this.y = y;
    }
}

export class ChartSeriesPointData {

    /**
     * Name of the series
     */
    public name: string;

    /**
     * Data
     */
    public data: ChartDataPoint [];

}

@Directive()
export abstract class AbstractChartComponent implements OnInit, OnDestroy, AfterViewInit {

    /**
     * Listens for updates for series data
     */
    @Input() seriesData: Observable<ChartSeries[]>;

    @Input() title: string;

    @Input() titleIndent: number = 30;

    @Input() legendVerticalAlign: "top" | "bottom" = "top";

    @Input() xAxisLabel: string;

    @Input() deriveXAxisLabel: boolean = true;

    @Input() chartType: string;

    @Input() colors: string[] = ["#7cb5ec", "#434348", "#90ed7d", "#f7a35c", "#8085e9", "#f15c80", "#e4d354", "#2b908f", "#f45b5b", "#91e8e1"];

    /**
     * Map of full qualified hierarchy names that map to logical child hierarchies.
     */
    @Input() childHierarchies: { [key: string]: string; };

    @Input() height: number = 400;

    @Input() enableSelection: boolean = true;

    @Output() memberSelection: EventEmitter<any> = new EventEmitter();

    subscriptions: Subscription = new Subscription();

    protected chart: Chart;

    /**
     * Data members used for MDX drilling and filtering. They align one for one with the labels
     */
    protected dataMembers: string[];
    protected dataLabels: string[];

    /**
     * Series types by series name
     */
    protected seriesDataTypes: { [key: string]: MeasureDataType; } = {};

    private _canvas: ElementRef;

    private pngLabel: string;
    private svgLabel: string;

    constructor(public translateService: TranslateService) {
    }

    ngOnInit(): void {
        this.translateService.get("wbo.bi.chart-download.svg").subscribe((translation: string) => {
            this.svgLabel = translation;
        });

        this.translateService.get("wbo.bi.chart-download.png").subscribe((translation: string) => {
            this.pngLabel = translation;
        });

        if (!this.chartType) {
            this.chartType = "column";
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    @ViewChild("chart")
    set canvas(value: ElementRef) {
        this._canvas = value;

        if (this._canvas) {
            this.subscriptions.add(this.seriesData.subscribe((seriesDataArr: ChartSeries[]) => {
                if (seriesDataArr && seriesDataArr.length > 0) {
                    if (seriesDataArr[0].dataLabels) {
                        this.dataMembers = seriesDataArr[0].dataMembers;
                        this.dataLabels = seriesDataArr[0].dataLabels;
                        if (this.deriveXAxisLabel && seriesDataArr[0].attribute != null) {
                            const dimension: string = seriesDataArr[0].attribute.replace(/\[([^\]]*)].*/, "$1");
                            const level: string = seriesDataArr[0].attribute.replace(/.*\[(.*)]$/, "$1");
                            const key: string = "wbo.bi.dimension-key." + dimension + ";" + level;
                            this.translateService.get(key).subscribe((translation: string) => {
                                this.xAxisLabel = translation;
                            });
                        }
                    }
                    seriesDataArr.forEach((seriesData: ChartSeries) => {
                        this.seriesDataTypes[seriesData.name] = seriesData.dataType;
                    });
                }

                this.setupData(seriesDataArr);
                this.createChart(this.defineChart());
                this.setupChart(seriesDataArr);
                this.chart.redraw();
            }));
        }
    }

    get canvas(): ElementRef {
        return this._canvas;
    }

    abstract setupData(seriesDataArr: ChartSeries[]): void;

    abstract setupChart(seriesDataArr: ChartSeries[]): void;

    abstract defineChart(): Options;

    ngAfterViewInit(): void {
        this.createChart(this.defineChart());
    }

    protected selectSingle(index: number) {
        if (!this.enableSelection || !this.dataMembers) {
            return;
        }

        if (!index) {
            index = 0;
        }
        const members: string [] = [];
        const labels: string[] = [];

        if (MdxMemberUtil.isMember(this.dataMembers[index])) {
            members.push(MdxMemberUtil.getHierarchyForMember(this.dataMembers[index], this.childHierarchies));
            labels.push(this.dataLabels[index]);
            this.memberSelection.emit(new ChartMemberSelection(this.title, members, labels));
        }
    }

    protected selectMulti(indexArr: number[]) {
        if (!this.enableSelection) {
            return;
        }

        if (indexArr && indexArr.length > 0 && this.dataMembers) {
            const members: string [] = [];
            const labels: string[] = [];

            indexArr.forEach((i: number) => {
                if (members.indexOf(this.dataMembers[i]) === -1) {
                    if (MdxMemberUtil.isMember(this.dataMembers[i])) {
                        members.push(MdxMemberUtil.getHierarchyForMember(this.dataMembers[i], this.childHierarchies));
                        labels.push(this.dataLabels[i]);
                    }
                }
            });

            if (members.length > 0) {
                this.memberSelection.emit(new ChartMemberSelection(this.title, members, labels));
            }
        }
    }

    protected drillToSelectionEvent(event: SelectEventObject): void {
        const selected = [];
        // Select points
        Highcharts.each(this.chart.series, function (series) {
            Highcharts.each(series.points, function (point) {
                if (point.x >= event.xAxis[0].min && point.x <= event.xAxis[0].max &&
                    point.y >= event.yAxis[0].min && point.y <= event.yAxis[0].max) {
                    selected.push(point.index);
                }
            });
        });

        this.selectMulti(selected);
    }

    protected createChart(options: Options): void {
        if (this.chart) {
            this.chart.destroy();
        }

        options.exporting = {
            fallbackToExportServer: false,
            menuItemDefinitions: {
                exportPng: {
                    text: this.pngLabel,
                    onclick: function () {
                        this.exportChartLocal();
                    }
                },
                exportSvg: {
                    text: this.svgLabel,
                    onclick: function () {
                        this.exportChartLocal({
                            type: "image/svg+xml"
                        });
                    }
                }
            },
            buttons: {
                contextButton: {
                    menuItems: ["exportPng", "exportSvg"]
                }
            }
        };

        this.chart = Highcharts.chart(this._canvas.nativeElement, options);
        this.chart.xAxis[0].setCategories(this.dataLabels);
    }
}
